/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { BooleanHelper } from "@bms/common";

interface IPlaylistEpisode {
  ContentUrl?: string;
  Title?: string;
  MediaNo?: number;
}

export class DownloadHelper {
  static isPodcastDownloadEnabled = BooleanHelper.toBool(
    process.env.REACT_APP_PODCAST_DOWNLOAD_ENABLED,
    false,
  );

  static sanitizeFileName(name: string) {
    return name
      .replace(/[\\/:*?"<>|]+/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  static getDownloadFileName(title?: string, parentTitle?: string) {
    const fileName = DownloadHelper.sanitizeFileName(
      [parentTitle, title].filter(Boolean).join(" - "),
    );
    const safeFileName = fileName || "podcast";

    return safeFileName.toLowerCase().endsWith(".mp3")
      ? safeFileName
      : `${safeFileName}.mp3`;
  }

  static getAlbumZipFileName(albumTitle?: string) {
    const fileName = DownloadHelper.sanitizeFileName(albumTitle || "album");

    return fileName.toLowerCase().endsWith(".zip")
      ? fileName
      : `${fileName}.zip`;
  }

  private static fetchCredentials(): RequestCredentials {
    return process.env.REACT_APP_PLAYER_WITH_CREDENTIALS === "true"
      ? "include"
      : "same-origin";
  }

  static async fetchContentBlob(contentUrl: string): Promise<Blob> {
    const response = await fetch(contentUrl, {
      credentials: DownloadHelper.fetchCredentials(),
    });

    if (!response.ok) {
      throw new Error("DOWNLOADS_FAILED_ALERT_MESSAGE");
    }

    return response.blob();
  }

  static saveBlob(blob: Blob, fileName: string) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
  }

  static async downloadSingleFile(
    contentUrl: string,
    title?: string,
    parentTitle?: string,
  ) {
    const blob = await DownloadHelper.fetchContentBlob(contentUrl);
    DownloadHelper.saveBlob(
      blob,
      DownloadHelper.getDownloadFileName(title, parentTitle),
    );
  }

  static getErrorMessageKey(error: unknown) {
    const apiError = error as {
      Message?: { MessageKey?: string };
      MessageKey?: string;
    };

    return (
      apiError?.MessageKey ||
      apiError?.Message?.MessageKey ||
      "DOWNLOADS_FAILED_ALERT_MESSAGE"
    );
  }

  static getPlaylistEpisodesWithContentUrl(
    playlist?: IPlaylistEpisode[],
  ): IPlaylistEpisode[] {
    if (!playlist?.length) {
      return [];
    }

    return playlist
      .filter((item) => !!item.ContentUrl)
      .sort((a, b) => (a.MediaNo ?? 0) - (b.MediaNo ?? 0));
  }
}
